/*---------------------------------------------------------------------------------------------
 *  Copyright (c) 2023 Vienna Hypertext Inc. All rights reserved.
 *--------------------------------------------------------------------------------------------*/

import { Socket as SocketType, io } from "socket.io-client";

import { Asset, EmitType, LogType } from "@vienna/common";

import { PlateGenerator } from "src/classes/plate/Generator/PlateGenerator";

export class GeneratorSocket {
  socket: SocketType;

  generator: PlateGenerator;

  constructor(generator: PlateGenerator) {
    this.generator = generator;
    const apiURL = process.env.API_URL;
    this.socket = io(`${apiURL}/generations`, {
      query: {
        jobId: this.generator.jobId,
      },
    });
    this.initializeListeners();
  }

  public emitConfirmation(confirmed: boolean, logId: string): void {
    this.socket.emit(EmitType.ConfirmActionResponse, {
      confirmed,
      logId,
    });
  }

  private initializeListeners() {
    this.socket.on(EmitType.RawData, (token: string) => {
      this.generator.logger.logDataToken(token);
    });

    this.socket.on(
      EmitType.Token,
      ({ token, logId, logType }: { token: string; logId: string; logType: LogType }) => {
        this.generator.logger.handleIncomingToken(token, logId, logType);
      }
    );

    this.socket.on(EmitType.Asset, (asset: Asset) => {
      this.generator.addAsset(asset);
    });

    this.socket.on(EmitType.ConfirmAction, ({ logId }: { logId: string }) => {
      this.generator.logger.requestConfirmation(logId);
    });

    this.socket.on(EmitType.Heartbeat, () => {
      this.generator.handleHeartbeat();
    });
  }

  public destroy(): void {
    this.socket.removeAllListeners();
    this.socket.close();
  }
}
